import {
  ChevronsUpDown,
  CircleUserRound,
  KeyRound,
  LogOut,
} from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { User } from "@/api/SolarCloudApi";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";

// export type User = {
//     lastLogin?: any;
//     addresses?: Array<UserAddress>;
//     updatedBy?: string;
//     updateVersion?: number;
//     joined?: any;
//     givenName?: string;
//     groups?: Array<string>;
//     uuid?: UUID;
//     userid?: string;
//     phoneNumbers?: Array<UserPhoneNumber>;
//     xeroId?: UUID;
//     updateAction?: Action;
//     emails?: Array<UserEmail>;
//     otherNames?: string;
//     organisationalRoles?: Array<OrganisationUser>;
//     organisations?: Array<Organisation>;
//     familyName?: string;
//     updated?: any;
//     status?: UserStatus;
// };

type NavUserProps = {
  user: User;
};

export function NavUser({ user }: NavUserProps) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { handleSignOut } = useAuth();

  const givenName = user.givenName ?? "";
  const familyName = user.familyName ?? "";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              className={cn(
                "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-6 [&>svg]:shrink-0",
                "h-12 text-sm group-data-[collapsible=icon]:p-0!"
              )}
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg">
                  {givenName.charAt(0)}
                  {familyName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {givenName} {familyName}
                </span>
                <span className="truncate text-xs">
                  {user.emails![0].address}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {givenName.charAt(0)}
                    {familyName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {givenName} {familyName}
                  </span>
                  <span className="truncate text-xs">
                    {user.emails![0].address}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <button
                className="cursor-pointer w-full"
                onClick={() => navigate("/dashboard/profile")}
              >
                <DropdownMenuItem>
                  <CircleUserRound />
                  My Profile
                </DropdownMenuItem>
              </button>
              <button
                className="cursor-pointer w-full"
                onClick={() => navigate("/dashboard/change-password")}
              >
                <DropdownMenuItem>
                  <KeyRound />
                  Change Password
                </DropdownMenuItem>
              </button>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <button
              className="cursor-pointer w-full"
              onClick={() => {
                navigate("/");
                handleSignOut();
              }}
            >
              <DropdownMenuItem>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </button>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
